import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button,
  Stepper, Step, StepLabel, Typography, Alert, LinearProgress,
  TextField, Grid, Chip, Paper, Avatar,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import FaceIcon from '@mui/icons-material/Face';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { scanIC } from '../../utils/ocrService';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const STEPS = [
  { label: 'Upload IC', icon: BadgeIcon },
  { label: 'Face Capture', icon: FaceIcon },
  { label: 'CTOS Verification', icon: VerifiedIcon },
];

const CTOS_STEPS = [
  'Submitting to CTOS...',
  'Validating IC against national registry...',
  'Running facial recognition match...',
  'Cross-checking financial records...',
  'Finalising verification...',
];

export default function KycVerificationDialog({ open, onClose, onComplete }) {
  const { user, updateUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: IC upload
  const [icFrontFile, setIcFrontFile] = useState(null);
  const [icBackFile, setIcBackFile] = useState(null);
  const [extractedName, setExtractedName] = useState('');
  const [extractedIc, setExtractedIc] = useState('');
  const [scanning, setScanning] = useState(false);

  // Step 2: face capture
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  // Step 3: CTOS simulation
  const [ctosStepIdx, setCtosStepIdx] = useState(0);
  const [ctosComplete, setCtosComplete] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setIcFrontFile(null);
      setIcBackFile(null);
      setExtractedName('');
      setExtractedIc('');
      setScanning(false);
      setCapturedPhoto(null);
      setCameraError(null);
      setCtosStepIdx(0);
      setCtosComplete(false);
      setResult(null);
    }
  }, [open]);

  // Start camera when entering step 2
  useEffect(() => {
    if (open && activeStep === 1 && !capturedPhoto) {
      (async () => {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
          setStream(s);
          if (videoRef.current) {
            videoRef.current.srcObject = s;
          }
          setCameraError(null);
        } catch (e) {
          setCameraError('Camera access denied or unavailable. You can still continue with a placeholder photo.');
        }
      })();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [open, activeStep]);

  // Run the CTOS simulation
  useEffect(() => {
    if (open && activeStep === 2 && !ctosComplete) {
      setCtosStepIdx(0);
      let i = 0;
      const timer = setInterval(() => {
        i += 1;
        if (i >= CTOS_STEPS.length) {
          clearInterval(timer);
          setCtosStepIdx(CTOS_STEPS.length);
          // Complete the verification
          completeVerification();
        } else {
          setCtosStepIdx(i);
        }
      }, 700);
      return () => clearInterval(timer);
    }
  }, [open, activeStep]);

  const completeVerification = async () => {
    const now = new Date().toISOString();

    // 1. Update user KYC fields
    const updated = db.users.update(user.id, {
      kycStatus: 'verified',
      idVerified: true,
      faceVerified: true,
      ctosVerified: true,
      kycSubmittedAt: now,
      kycVerifiedAt: now,
      // If OCR extracted any values, update those too
      ...(extractedName && { name: extractedName }),
      ...(extractedIc && { icNumber: extractedIc }),
    });

    // 2. Release pending rewards
    const released = db.engine.releasePendingRewards(user.id);

    // 3. Award eKYC task bonus
    const taskResult = db.engine.awardTaskBonus(user.id, 'ekyc_completed', user);

    // 4. Audit
    db.auditLogs.create({
      userId: user.id,
      userName: user.name,
      userRole: user.role,
      action: 'kyc_verified',
      entityType: 'user',
      entityId: user.id,
      description: 'eKYC verification completed via CTOS',
    });

    // 5. Sync into auth context so all pages refresh
    const freshUser = db.users.getById(user.id);
    if (freshUser) {
      const { password: _pw, ...safe } = freshUser;
      updateUser?.(safe);
    }

    setResult({
      released,
      taskBonus: taskResult,
      user: updated,
    });
    setCtosComplete(true);
  };

  // ── Step 1 handlers ────────────────────────────────
  const handleIcUpload = async (side, file) => {
    if (!file) return;
    if (side === 'front') {
      setIcFrontFile(file);
      setScanning(true);
      try {
        const res = await scanIC(file);
        if (res.fullName) setExtractedName(res.fullName);
        if (res.icNumber) setExtractedIc(res.icNumber);
      } catch (e) { /* ignore */ }
      setScanning(false);
    } else {
      setIcBackFile(file);
    }
  };

  const canProceedStep1 = icFrontFile && extractedName && extractedIc;

  // ── Step 2 handlers ────────────────────────────────
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      // Fallback: use a placeholder image
      setCapturedPhoto('placeholder');
      return;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setCapturedPhoto(canvas.toDataURL('image/jpeg'));
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  const retakePhoto = async () => {
    setCapturedPhoto(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      setCameraError('Could not restart camera.');
    }
  };

  // ── Render ─────────────────────────────────────────
  const renderStep1 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Upload a clear photo of your Malaysian IC (MyKad). We'll automatically extract your name and IC number.
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center',
              backgroundColor: icFrontFile ? '#F0FFF4' : '#FAFBFC',
              borderColor: icFrontFile ? SECONDARY : '#C4CDD5',
              position: 'relative',
            }}
          >
            {scanning && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderRadius: '8px 8px 0 0' }} />}
            <UploadFileIcon sx={{ fontSize: 36, color: icFrontFile ? SECONDARY : '#90A4AE', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>IC Front *</Typography>
            <Button variant="outlined" size="small" component="label" disabled={scanning}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}>
              {scanning ? 'Scanning...' : (icFrontFile ? 'Re-upload' : 'Choose File')}
              <input type="file" hidden accept="image/*,.pdf"
                onChange={(e) => handleIcUpload('front', e.target.files?.[0])} />
            </Button>
            {icFrontFile && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: SECONDARY }}>
                ✓ {icFrontFile.name}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center',
              backgroundColor: icBackFile ? '#F0FFF4' : '#FAFBFC',
              borderColor: icBackFile ? SECONDARY : '#C4CDD5',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 36, color: icBackFile ? SECONDARY : '#90A4AE', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>IC Back</Typography>
            <Button variant="outlined" size="small" component="label"
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}>
              {icBackFile ? 'Re-upload' : 'Choose File'}
              <input type="file" hidden accept="image/*,.pdf"
                onChange={(e) => handleIcUpload('back', e.target.files?.[0])} />
            </Button>
            {icBackFile && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: SECONDARY }}>
                ✓ {icBackFile.name}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1, mt: 1 }}>
            Extracted Information (editable)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name *"
                value={extractedName} onChange={(e) => setExtractedName(e.target.value)}
                placeholder="Auto-filled from IC scan" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="IC Number *"
                value={extractedIc} onChange={(e) => setExtractedIc(e.target.value)}
                placeholder="Auto-filled from IC scan"
                inputProps={{ maxLength: 14 }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Position your face inside the frame and click "Capture Photo".
        We'll match it against your IC photo.
      </Alert>

      {!capturedPhoto ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2, textAlign: 'center', backgroundColor: '#000',
            minHeight: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {cameraError ? (
            <>
              <FaceIcon sx={{ fontSize: 72, color: '#546E7A', mb: 2 }} />
              <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                {cameraError}
              </Typography>
            </>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', maxWidth: 480, borderRadius: 8 }}
            />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <Button
            variant="contained" startIcon={<CameraAltIcon />} onClick={capturePhoto}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none', backgroundColor: SECONDARY, '&:hover': { backgroundColor: '#00A37D' } }}
          >
            Capture Photo
          </Button>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2, textAlign: 'center',
            backgroundColor: '#F0FFF4', borderColor: SECONDARY,
          }}
        >
          {capturedPhoto === 'placeholder' ? (
            <Avatar sx={{ width: 120, height: 120, mx: 'auto', mb: 2, backgroundColor: '#E0E0E0' }}>
              <FaceIcon sx={{ fontSize: 72, color: '#90A4AE' }} />
            </Avatar>
          ) : (
            <img
              src={capturedPhoto}
              alt="Captured"
              style={{ width: '100%', maxWidth: 320, borderRadius: 8, border: `2px solid ${SECONDARY}` }}
            />
          )}
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Photo captured"
            size="small"
            sx={{ mt: 2, backgroundColor: SECONDARY, color: 'white', fontWeight: 700 }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<ReplayIcon />}
              onClick={retakePhoto}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Retake
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      {!ctosComplete ? (
        <>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, backgroundColor: '#E3F2FD' }}>
            <VerifiedIcon sx={{ fontSize: 40, color: '#1565C0' }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Verifying with CTOS...
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Please wait while we validate your identity
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(ctosStepIdx / CTOS_STEPS.length) * 100}
            sx={{ borderRadius: 2, mb: 2 }}
          />
          <Box sx={{ textAlign: 'left', mt: 2 }}>
            {CTOS_STEPS.map((step, i) => (
              <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {i < ctosStepIdx ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: SECONDARY }} />
                ) : i === ctosStepIdx ? (
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #1565C0', borderTopColor: 'transparent', animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                ) : (
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#E0E0E0' }} />
                )}
                <Typography variant="caption" sx={{
                  color: i < ctosStepIdx ? '#2E7D32' : i === ctosStepIdx ? PRIMARY : '#90A4AE',
                  fontWeight: i <= ctosStepIdx ? 600 : 400,
                }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <>
          <Avatar sx={{ width: 96, height: 96, mx: 'auto', mb: 2, backgroundColor: '#E8F5E9' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: SECONDARY }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: SECONDARY }}>
            Verification Successful!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your identity has been verified by CTOS. You can now earn tokens and commissions.
          </Typography>
          {result?.taskBonus && !result.taskBonus.alreadyEarned && (
            <Alert severity="success" sx={{ borderRadius: 2, mb: 1 }}>
              🎉 You earned <strong>+{result.taskBonus.amount} tokens</strong> as your eKYC completion bonus!
            </Alert>
          )}
          {result?.released?.released > 0 && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {result.released.released} pending reward(s) totalling <strong>+{result.released.amount} tokens</strong> have been released into your balance.
              {result.released.cases > 0 && ` ${result.released.cases} commission case(s) also released.`}
            </Alert>
          )}
        </>
      )}
    </Box>
  );

  const handleNext = () => {
    if (activeStep === 0 && !canProceedStep1) return;
    if (activeStep === 1 && !capturedPhoto) return;
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleFinish = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    onComplete?.(result);
    onClose?.();
  };

  const handleCancel = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={ctosComplete ? handleFinish : handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>eKYC Identity Verification</Box>
        <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
          CTOS-powered identity check
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((step) => {
            const StepIcon = step.icon;
            return (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar sx={{
                      width: 36, height: 36,
                      backgroundColor: STEPS.indexOf(step) <= activeStep ? PRIMARY : '#E0E0E0',
                      color: STEPS.indexOf(step) <= activeStep ? 'white' : '#90A4AE',
                    }}>
                      <StepIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                >
                  <Typography variant="caption">{step.label}</Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {!ctosComplete ? (
          <>
            <Button onClick={handleCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
            {activeStep > 0 && activeStep < 2 && (
              <Button onClick={handleBack} sx={{ textTransform: 'none' }}>Back</Button>
            )}
            {activeStep < 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={(activeStep === 0 && !canProceedStep1) || (activeStep === 1 && !capturedPhoto)}
                sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}
              >
                {activeStep === 1 ? 'Start CTOS Check' : 'Next'}
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleFinish}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: SECONDARY }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
